/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Accordion, Spinner } from "react-bootstrap";
import { Button, Card } from "core/components";
import { useFormContext, useFieldArray } from "react-hook-form";
import TemplateTaskForm from "./TemplateTaskForm";
import { TaskFormData, TemplateTaskFormData } from "core/schema";
import TemplateTasksContext from "./TemplateTasksContext";

type TemplateTasksFormProps = {
  tasks: Array<TaskFormData>;
  is_purchasing: boolean;
};

const TemplateTasksForm = ({ tasks, items, templateTasks, is_purchasing }: TemplateTasksFormProps): JSX.Element => {
  const { control, resetField } = useFormContext();
  const { fields, append, remove } = useFieldArray<TemplateTaskFormData>({
    control,
    name: "template_tasks_attributes",
    keyName: "key"
  });
  const [activeKeys, setActiveKeys] = React.useState<Array<string>>(["0"]);
  const [selectedTasks, setSelectedTasks] = React.useState<Array<TaskFormData>>([]);
  const [loading, setLoading] = React.useState(false);

  const getDefaultActiveKey = (array) => {
    const lastIndex = array.length;

    return lastIndex <= 0 ? 0 : lastIndex;
  };

  const canAppendTask = () => {
    return fields.length < tasks.length && !is_purchasing;
  };

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 pt-lg-8 pb-lg-8">
        <Spinner animation="border" role="status" variant="brand">
          <span className="visually-hidden">Loading Template Tasks...</span>
        </Spinner>
      </div>
    );
  };

  React.useEffect(() => {
    if (templateTasks.length) {
      setLoading(true);
      setTimeout(() => {
        resetField("template_tasks_attributes", { defaultValue: templateTasks });
        setLoading(false);
      }, 100);
    }
  }, [templateTasks]);

  return (
    <React.StrictMode>
      <TemplateTasksContext.Provider value={{ selectedTasks, setSelectedTasks }}>
        <div className="sticky-card-container">
          <Card>
            <Card.Header title="Tasks">
              <h4 className="mr-3 h">Total Weight: {fields.reduce((sum, task) => sum + (parseFloat(task.weight) || 0), 0)}</h4>
            </Card.Header>
            <Card.Body>
              {loading ? (
                loader()
              ) : (
                <>
                  <Accordion alwaysOpen activeKey={activeKeys} onSelect={(e) => setActiveKeys(e)}>
                    {fields.map((item, index) => (
                      <TemplateTaskForm key={item.key} index={index} remove={remove} is_purchasing={is_purchasing} tasks={tasks} items={items} />
                    ))}
                  </Accordion>
                  {canAppendTask() && (
                    <Button
                      className="mt-4 button--brand"
                      title="Add Task"
                      icon="plus"
                      type="button"
                      onClick={() => {
                        append({});
                        setActiveKeys([getDefaultActiveKey(fields).toString()]);
                      }}
                    />
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </div>
      </TemplateTasksContext.Provider>
    </React.StrictMode>
  );
};

export default TemplateTasksForm;
